import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Upload } from "lucide-react";
import Spinner from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  ACCEPT_EXTENSIONS_STRING,
  ATTACHMENT_MAX_FILE_SIZE_MB,
} from "@/constants";
import { FilePreviewDialog } from "@/components/file-preview/FilePreviewDialog";
import type { QuestionnaireAttachmentMeta } from "../../hooks/attachment";
import { useQuestionnaireAttachmentField } from "../../hooks";
import type { AttachmentFieldName } from "../../types/attachments";
import { OperationProgressStatus } from "@/components/progress";
import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import { QuestionnaireAttachmentProgressDialog } from "./attachment/QuestionnaireAttachmentProgressDialog";
import { QuestionnaireAttachmentDeleteDialog } from "./attachment/QuestionnaireAttachmentDeleteDialog";
import { QuestionnaireAttachmentUploadDialog } from "./attachment/QuestionnaireAttachmentUploadDialog";
import { QuestionnaireAttachmentItem } from "./attachment/QuestionnaireAttachmentItem";
import { useFileDragAndDrop } from "@/hooks/useFileDragAndDrop";

interface QuestionnaireAttachmentFieldProps {
  name: AttachmentFieldName;
  label: string;
  existingAttachments?: QuestionnaireAttachmentMeta[];
  disabled?: boolean;
}

export const QuestionnaireAttachmentField = memo(
  function QuestionnaireAttachmentField({
    name,
    label,
    existingAttachments = [],
    disabled = false,
  }: QuestionnaireAttachmentFieldProps) {
    const { t } = useTranslation();

    const {
      fileInputRef,
      isProcessing,
      operationProgress,
      deleteOperationProgress,
      attachmentItems,
      expandedKeys,
      preview,
      handleAddFilesClick,
      handleFileUpload,
      handleRemove,
      toggleInlinePreview,
      openPreviewDialog,
      closePreview,
      pendingDelete,
      confirmDelete,
      cancelDelete,
      pendingUpload,
      confirmUpload,
      cancelUpload,
      handleDrop,
    } = useQuestionnaireAttachmentField(name, existingAttachments);

    const isFieldDisabled = disabled || isProcessing;

    const { isDragging, dragHandlers } = useFileDragAndDrop({
      onDrop: handleDrop,
      isDisabled: isFieldDisabled,
    });

    const emptyState = attachmentItems.length === 0;

    return (
      <FormField
        name={name}
        render={() => (
          <FormItem>
            <div id={name} className="space-y-4">
              <QuestionnaireAttachmentProgressDialog
                open={operationProgress.status !== OperationProgressStatus.IDLE}
                operationProgress={operationProgress}
                currentItemLabel={operationProgress.currentItemLabel}
                translationKey="fileAttachments.uploadProgress"
                onClose={operationProgress.reset}
              />

              <QuestionnaireAttachmentProgressDialog
                open={
                  deleteOperationProgress.status !==
                  OperationProgressStatus.IDLE
                }
                operationProgress={deleteOperationProgress}
                currentItemLabel={deleteOperationProgress.currentItemLabel}
                translationKey="fileAttachments.deleteProgress"
                onClose={deleteOperationProgress.reset}
              />

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">{label}</label>
              </div>

              <Input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_EXTENSIONS_STRING}
                onChange={handleFileUpload}
                className="hidden"
              />

              <div
                className={cn(
                  "flex min-h-[7rem] flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors px-4 py-4",
                  isDragging && "border-blue-400 bg-blue-50",
                  isFieldDisabled &&
                    !isDragging &&
                    "border-blue-200 bg-blue-50",
                  !isFieldDisabled &&
                    !isDragging &&
                    "border-slate-200 hover:border-slate-300",
                  !isFieldDisabled && "cursor-pointer",
                  disabled && "opacity-50 cursor-not-allowed",
                )}
                onClick={!isFieldDisabled ? handleAddFilesClick : undefined}
                {...dragHandlers}
              >
                {isProcessing ? (
                  <Spinner size="md" className="text-blue-500 shrink-0" />
                ) : (
                  <Upload
                    className={cn(
                      "h-6 w-6 shrink-0",
                      isDragging ? "text-blue-500" : "text-slate-400",
                    )}
                  />
                )}
                <div className="flex flex-col items-center justify-center gap-0.5 text-center">
                  <p className="text-sm font-medium text-slate-700">
                    {isProcessing
                      ? t("attachmentField.processingFiles")
                      : isDragging
                        ? t("attachmentField.dropFiles")
                        : t("attachmentField.addMoreFiles")}
                  </p>
                  <p className="text-xs text-slate-500">
                    {t("attachmentField.supportedFormats", {
                      maxSize: ATTACHMENT_MAX_FILE_SIZE_MB,
                    })}
                  </p>
                </div>
              </div>

              {!emptyState && (
                <div className="space-y-1">
                  {attachmentItems.map((item) => {
                    const { key, url, field } = item;
                    const isExpanded = expandedKeys.has(key);

                    return (
                      <QuestionnaireAttachmentItem
                        key={key}
                        item={item}
                        isExpanded={isExpanded}
                        url={url ?? undefined}
                        disabled={isFieldDisabled}
                        onRemove={() => handleRemove(item)}
                        onPreview={() =>
                          url && openPreviewDialog(url, field.filename)
                        }
                        onToggleExpand={() => toggleInlinePreview(key)}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            <FormMessage />

            {preview && preview.fileUrl && (
              <FilePreviewDialog
                open
                onClose={closePreview}
                fileUrl={preview.fileUrl}
                fileName={preview.fileName}
              />
            )}

            <QuestionnaireAttachmentDeleteDialog
              open={Boolean(pendingDelete)}
              filename={pendingDelete?.filename ?? undefined}
              onConfirm={confirmDelete}
              onClose={cancelDelete}
            />

            <QuestionnaireAttachmentUploadDialog
              open={Boolean(pendingUpload)}
              fileCount={pendingUpload?.length || 0}
              onConfirm={confirmUpload}
              onClose={cancelUpload}
            />
          </FormItem>
        )}
      />
    );
  },
);
